import { Rpc } from '@dynamika/rpc-core/contracts';
import type { Handler, Listener } from '@dynamika/rpc-core/engine';
import { emitToViewer, requestToViewer, RpcRequestOptions } from '../helpers';
import { getPeer } from '../peer';

export const viewerRpcScheme = {
  requests: {
    measurement: {
      activate: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.ACTIVATE,
          payload,
          options
        ),
      deactivate: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.DEACTIVATE,
          payload,
          options
        ),
      lock: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.LOCK, payload, options),
      unlock: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.UNLOCK, payload, options),
      delete: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.DELETE, payload, options),
      removeAnnotation: (annotationUID: string, options?: RpcRequestOptions) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.REMOVE_ANNOTATION,
          { annotationUID },
          options
        ),
      removeAllAnnotations: (
        annotationUIDs: string[],
        options?: RpcRequestOptions
      ) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.REMOVE_ALL_ANNOTATIONS,
          { annotationUIDs },
          options
        ),
      screenshot: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.SCREENSHOT,
          payload,
          options
        ),
      show: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.SHOW, payload, options),
      hide: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.HIDE, payload, options),
      revive: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Measurement.REVIVE, payload, options),
      relabelAnnotation: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(
          Rpc.Methods.Viewer.Measurement.RELABEL_ANNOTATION,
          payload,
          options
        ),
    },
    study: {
      setDetached: (payload: unknown, options?: RpcRequestOptions) =>
        requestToViewer(Rpc.Methods.Viewer.Study.SET_DETACHED, payload, options),
    },
  },
  handlers: {
    process: {
      onRun: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.Process.RUN, cb),
      onList: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.Process.LIST, cb),
      onPoll: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.Process.POLL, cb),
    },
    dicomFile: {
      onGet: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.DicomFile.GET, cb),
    },
    edtf: {
      onOpen: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.Edtf.OPEN, cb),
    },
    dicomAudit: {
      onDownload: (cb: Handler) =>
        getPeer().handle(Rpc.Methods.D7.DicomAudit.DOWNLOAD, cb),
    },
    user: {
      onGet: (cb: Handler) => getPeer().handle(Rpc.Methods.D7.User.GET, cb),
    },
    segmentation: {
      onLock: (cb: Handler) =>
        getPeer().handle(Rpc.Methods.D7.Segmentation.LOCK, cb),
      onUnlock: (cb: Handler) =>
        getPeer().handle(Rpc.Methods.D7.Segmentation.UNLOCK, cb),
      onDelete: (cb: Handler) =>
        getPeer().handle(Rpc.Methods.D7.Segmentation.DELETE, cb),
    },
  },
  events: {
    setUser: (payload: unknown) => emitToViewer(Rpc.Events.D7.SET_USER, payload),
    // Announced, not requested: the viewer iframe reloads on every visit switch
    // and cannot answer for ~1s — waiting for a reply killed the DICOMViewerSaga.
    loadStudy: (payload: unknown) =>
      emitToViewer(Rpc.Events.D7.LOAD_STUDY, payload),
    dirtyState: (payload: unknown) => emitToViewer(Rpc.Events.D7.DIRTY_STATE, payload),
    deleteMeasurement: (payload: unknown) =>
      emitToViewer(Rpc.Events.D7.DELETE_MEASUREMENT, payload),
    processesPolled: (payload: unknown) =>
      emitToViewer(Rpc.Events.D7.PROCESSES_POLLED, payload),
  },
  listeners: {
    onReady: (listener: Listener) => getPeer().on(Rpc.Events.Viewer.READY, listener),
    onStartListening: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.START_LISTENING, listener),
    onClosed: (listener: Listener) => getPeer().on(Rpc.Events.Viewer.CLOSED, listener),
    onAttachForm: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.ATTACH_FORM, listener),
    onBusyChanged: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.BUSY_CHANGED, listener),
    onSeriesChanged: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.SERIES_CHANGED, listener),
    onStoreMeasurement: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.STORE_MEASUREMENT, listener),
    onSegmentationChanged: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.SEGMENTATION_CHANGED, listener),
    onSegmentationSelected: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.SEGMENTATION_SELECTED, listener),
    onSegmentationSaved: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.SEGMENTATION_SAVED, listener),
    onSelectedLabels: (listener: Listener) =>
      getPeer().on(Rpc.Events.Viewer.SELECTED_LABELS, listener),
  },
};
